<template>
  <collapsible-section class="_pt0 _pb0" :collapsed="false">
    <template #headerText>
      <p class="color-leaf">Member Demographics</p>
    </template>
    <template #headerActions>
      <app-column-picker
        v-if="showOtherAddresses && computedAddresses.length !== 0"
        :headers="headers"
        @updateHeaders="updateHeaders"
        />
      <button
        type="button"
        v-if="computedAddressFormVisible && editModeEnabled"
        @click="clear"
        class="btn --sm white"
      >
        Clear
      </button>
      <div
        v-if="
          editable && !hideAddAnotherAddressButton
        "
      >
        <button
          type="button"
          @click="addAnotherAddress"
          class="btn --sm white _mrxs"
        >
          Add Another Address
        </button>
      </div>
      <div>
        <button
          type="button"
          v-if="showUpdateButton"
          @click="updateAddress"
          class="btn --sm white _mrxs"
        >
          Update Address
        </button>
      </div>
    </template>
    <template #collapsibleSection class="advanced-table-section-header">
      <async-loader v-if="loading" />
      <div v-else container>
        <div row>
          <div v-if="computedAddressFormVisible" class="_mbs">
            <div grid v-if="showNameFields">
              <div column="3">
                <app-labeled-data
                  :data='$v.firstName.$model'
                  label="First Name:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  :required='isForeignOrUS'
                  :errorMessage="firstNameError"
                  :invalid="$v.firstName.$invalid"
                  v-model="$v.firstName.$model"
                  valueKey="firstName"
                  label="First Name:"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='middleInitial'
                  label="Middle Initial:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  :disabled="computedFieldsDisabled"
                  v-model="middleInitial"
                  valueKey="middleInitial"
                  label="Middle Initial:"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.lastName.$model'
                  label="Last Name:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  :required='isForeignOrUS'
                  :errorMessage="lastNameError"
                  :invalid="$v.lastName.$invalid"
                  :disabled="computedFieldsDisabled"
                  v-model="$v.lastName.$model"
                  valueKey="lastName"
                  label="Last Name:"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='suffix'
                  label="Suffix"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  :disabled="computedFieldsDisabled"
                  v-model="suffix"
                  name="suffix"
                  label="Suffix:"
                  @input="addressUpdated"
                />
              </div>
            </div>
            <div grid>
              <div column="3">
                <app-labeled-data
                  :data='locatedInUS'
                  label="Located In US?"
                  v-if="computedFieldsDisabled"
                  />
                <app-select
                  v-else
                  v-model="locatedInUS"
                  valueKey="locatedInUS"
                  :options="yesNoOptions"
                  :disabled="computedFieldsDisabled"
                  label="Located In US?"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.line1.$model'
                  label="Address Line 1:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  valueKey="line1"
                  label="Address Line 1:"
                  v-model="$v.line1.$model"
                  :required="internalAddressLine1Required || isForeignOrUS"
                  :errorMessage="address1Error"
                  :invalid="$v.line1.$invalid"
                  :disabled="computedFieldsDisabled"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.line2.$model'
                  label="Address Line 2:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  v-model="$v.line2.$model"
                  :errorMessage="address2Error"
                  :invalid="$v.line2.$invalid"
                  :disabled="computedFieldsDisabled"
                  valueKey="line2"
                  label="Address Line 2:"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.line3.$model'
                  label="Address Line 3:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  v-model="$v.line3.$model"
                  :errorMessage="address3Error"
                  :invalid="$v.line3.$invalid"
                  :disabled="computedFieldsDisabled"
                  valueKey="line3"
                  label="Address Line 3:"
                  @input="addressUpdated"
                />
              </div>
            </div>
            <div grid>
              <div column="3">
                <app-labeled-data
                  :data='$v.zip.$model'
                  label="Zip Code:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  v-model="$v.zip.$model"
                  valueKey="zip"
                  label="Zip Code:"
                  :required="isLocatedInUS"
                  :errorMessage="zipError"
                  :invalid="$v.zip.$invalid"
                  :disabled="computedFieldsDisabled"
                  @input="zipCodeUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.city.$model'
                  label="City:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  v-model="$v.city.$model"
                  valueKey="city"
                  label="City:"
                  :required="isLocatedInUS"
                  :errorMessage="cityError"
                  :invalid="$v.city.$invalid"
                  :disabled="computedFieldsDisabled"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.state.$model'
                  label="State:"
                  v-if="computedFieldsDisabled"
                  />
                <app-select
                  v-else
                  v-model="$v.state.$model"
                  name="state"
                  :options="stateOptions"
                  label="State:"
                  :required="isLocatedInUS"
                  :errorMessage="stateError"
                  :invalid="$v.state.$invalid"
                  :disabled="computedFieldsDisabled"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.county.$model'
                  label="County:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  v-model="$v.county.$model"
                  :errorMessage="countyError"
                  :invalid="$v.county.$invalid"
                  :disabled="computedFieldsDisabled"
                  valueKey="county"
                  label="County:"
                  @input="addressUpdated"
                />
              </div>
            </div>
            <div grid>
              <div column="3">
                <app-labeled-data
                  :data='$v.phone.$model'
                  label="Phone Number:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  v-model="$v.phone.$model"
                  :errorMessage="phoneError"
                  :invalid="$v.phone.$invalid"
                  :disabled="computedFieldsDisabled"
                  valueKey="phone"
                  label="Phone Number:"
                  @input="addressUpdated"
                />
              </div>
              <div column="3">
                <app-labeled-data
                  :data='$v.email.$model'
                  label="Email Address:"
                  v-if="computedFieldsDisabled"
                  />
                <app-input
                  v-else
                  v-model="$v.email.$model"
                  :errorMessage="emailError"
                  :invalid="$v.email.$invalid"
                  :disabled="computedFieldsDisabled"
                  valueKey="email"
                  label="Email Address:"
                  @input="addressUpdated"
                />
              </div>
            </div>
          </div>
          <div v-if="showOtherAddresses">
            <app-advanced-table
              v-if="computedAddresses.length !== 0 || !editable"
              title="Group Results"
              class="_pt0 _pb0"
              :headers="headers"
              :items="computedAddresses"
              :rowsPerPage="3"
            >
              <template
                #items="{ item, visibleHeaders, getCellValue }"

              >
                <tr tabindex="0">
                  <td v-for="(header, index) in visibleHeaders" :key="index">
                    <template v-if="header.value === 'action'">
                      <a
                        v-if="!isAddressBeingEdited(item)"
                        @click="editAddress(item)"
                        >Select</a
                      >
                    </template>
                    <template v-else-if="header.value === 'addressType'">
                      <font-awesome-icon
                        v-if="convertAddressType(item) === addressType"
                        :icon="['fa', 'check']" aria-label="check"
                      />
                    </template>
                    <template v-else>
                      {{ getCellValue(item, header) }}
                    </template>
                  </td>
                </tr>
              </template>
            </app-advanced-table>
          </div>
        </div>
      </div>
    </template>
  </collapsible-section>
</template>

<script>
  import CollapsibleSection from '@sections/CollapsibleSection'
  import AppAdvancedTable from '@organisms/AdvancedTable'
  import AppColumnPicker from '@molecules/ColumnPicker.vue'
  import AppInput from '@atoms/Input'
  import AppSelect from '@atoms/Select'
  import AppLabeledData from '@molecules/LabeledData'
  import AsyncLoader from '@molecules/AsyncLoader'

  import SelectOptions from '@mixins/SelectOptions'
  import validationMessages from '@mixins/validationMessages'
  import ZipCodeHelper from '@mixins/ZipCodeHelper'
  import formReady from '@mixins/formReady'

  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
  import { foreignIndicatorToLocatedInUS, convertAddressType, fiveOrNineDigitsLong } from '@/utils'

  import {
    requiredIf,
    numeric,
    minLength,
    maxLength,
    email
  } from 'vuelidate/lib/validators'

  const PRIMARY_ADDRESS_CODE = 'H'

  export default {
    mixins: [
      SelectOptions,
      validationMessages,
      formReady,
      ZipCodeHelper
    ],
    components: {
      CollapsibleSection,
      AppAdvancedTable,
      AppColumnPicker,
      AppInput,
      AppSelect,
      AppLabeledData,
      FontAwesomeIcon,
      AsyncLoader
    },
    props: {
      showForm: {
        type: Boolean,
        default: false
      },
      value: {
        type: Object,
        default() {
          return {}
        }
      },
      showOtherAddresses: {
        type: Boolean,
        default: true
      },
      addresses: {
        type: Array,
        default() {
          return []
        }
      },
      loading: Boolean,
      showNameFields: {
        type: Boolean,
        default: true
      },
      canEditHomeAddress: {
        type: Boolean,
        default: true
      },
      editable: {
        type: Boolean,
        default: false
      },
      editModeEnabledByDefault: {
        type: Boolean,
        default: false
      },
      hideAddAnotherAddressButton: {
        type: Boolean,
        default: false
      },
      addressLine1Required: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        PRIMARY_ADDRESS_CODE,
        internalAddresses: this.addresses,
        internalShowForm: this.showForm,
        firstName: this.value.firstName || '',
        middleInitial: this.value.middleInitial || '',
        lastName: this.value.lastName || '',
        suffix: this.value.suffix || '',
        email: this.value.email || '',
        phone: this.value.phone || '',
        line1: this.value.line1 || '',
        line2: this.value.line2 || '',
        line3: this.value.line3 || '',
        locatedInUS: foreignIndicatorToLocatedInUS(this.value.foreignIndicator, this.value.locatedInUS, 'yes'),
        zip: this.value.zip || '',
        city: this.value.city || '',
        state: this.value.state || '',
        county: this.value.county || '',
        addressType: convertAddressType(this.value),
        internalAddressLine1Required: this.addressLine1Required,
        editModeEnabled: this.editModeEnabledByDefault,
        headers: [
          {
            text: 'Current',
            value: 'addressType'
          },
          { text: 'Line 1', value: 'line1' },
          { text: 'Line 2', value: 'line2', visible: false },
          { text: 'City', value: 'city' },
          { text: 'State', value: 'state' },
          { text: 'Zip', value: 'zip' },
          { text: 'Phone', value: 'homePhoneNumber' },
          { text: 'Email', value: 'email' },
          { text: 'Action', value: 'action', visible: this.editable }
        ],
        useLocatedInUSDefaultValue: true
      }
    },
    methods: {
      convertAddressType,
      zipCodeUpdated() {
        if (this.$v.zip.$invalid) {
          return
        }

        this.suggestLocationFromZipCode(this.zip)
          .then((suggestion) => {
            if (!suggestion) {
              return
            }

            if (suggestion.city) {
              this.city = suggestion.city
            }

            if (suggestion.state) {
              this.state = suggestion.state
            }

            if (suggestion.county) {
              this.county = suggestion.county
            }

            this.addressUpdated()
          })
      },
      isAddressBeingEdited(address) {
        return convertAddressType(address) === this.addressType
      },
      addAnotherAddress() {
        this.clear()
        this.internalShowForm = true
        this.editModeEnabled = true
      },
      editAddress(cell) {
        this.assignCell(cell)
        this.addressUpdated()
        this.internalShowForm = true
      },
      assignCell(cell) {
        this.line1 = cell.line1
        this.line2 = cell.line2
        this.line3 = cell.line3
        this.phone = cell.phone
        this.email = cell.email
        this.zip = cell.zip
        this.city = cell.city
        this.state = cell.state
        this.county = cell.county
        this.addressType = convertAddressType(cell)
        this.locatedInUS = foreignIndicatorToLocatedInUS(cell.foreignIndicator, cell.locatedInUS, 'yes')

        this.addressUpdated()
      },
      clear() {
        this.firstName = ''
        this.middleInitial = ''
        this.lastName = ''
        this.suffix = ''
        this.email = ''
        this.phone = ''
        this.line1 = ''
        this.line2 = ''
        this.line3 = ''
        this.zip = ''
        this.city = ''
        this.state = ''
        this.county = ''
        this.addressType = ''
      },
      updateHeaders(headers) {
        this.headers = headers
      },
      addressUpdated() {
        this.$emit('input', {
          firstName: this.firstName,
          middleInitial: this.middleInitial,
          lastName: this.lastName,
          suffix: this.suffix,
          email: this.email,
          phone: this.phone,
          line1: this.line1,
          line2: this.line2,
          line3: this.line3,
          zip: this.zip,
          city: this.city,
          state: this.state,
          county: this.county,
          addressType: this.addressType,
          locatedInUS: this.locatedInUS
        })
      },
      updateAddress() {
        this.editModeEnabled = true
      }
    },
    validations: {
      firstName: {
        required: requiredIf(function() {
          return this.isForeignOrUS && this.showNameFields
        }),
        maxLength: maxLength(15)
      },
      lastName: {
        required: requiredIf(function() {
          return this.isForeignOrUS && this.showNameFields
        }),
        maxLength: maxLength(35)
      },
      email: {
        maxLength: maxLength(40),
        email
      },
      phone: {
        minLength: minLength(10),
        maxLength: maxLength(10),
        numeric
      },
      zip: {
        required: requiredIf(function() {
          return this.isLocatedInUS
        }),
        numeric,
        fiveOrNineDigitsLong
      },
      city: {
        required: requiredIf(function() {
          return this.isLocatedInUS
        }),
        maxLength: maxLength(19)
      },
      state: {
        required: requiredIf(function() {
          return this.isLocatedInUS
        })
      },
      county: {
        maxLength: maxLength(20)
      },
      line1: {
        required: requiredIf(function() {
          return this.internalAddressLine1Required && this.isForeignOrUS
        }),
        maxLength: maxLength(40)
      },
      line2: {
        maxLength: maxLength(40)
      },
      line3: {
        maxLength: maxLength(40)
      }
    },
    computed: {
      ready () {
        // if edit mode is NOT enabled, (eg: it's showing the current address in read only mode on Member Edit), this
        // component is considered ready
        return !this.$v.$invalid || !this.editModeEnabled
      },
      address1Error() {
        if (!this.$v.line1.required) {
          return 'required'
        }
        if (!this.$v.line1.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      address2Error() {
        if (!this.$v.line2.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      address3Error() {
        if (!this.$v.line3.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      emailError() {
        if (!this.$v.email.email) {
          return 'Not a valid email address'
        } else if (!this.$v.email.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      phoneError() {
        if (!this.$v.phone.numeric) {
          return 'must be numeric'
        } else if (!this.$v.phone.maxLength) {
          return 'character limit exceeded'
        } else if (!this.$v.phone.minLength) {
          return 'must be 10 characters'
        }
        return null
      },
      stateError() {
        if (!this.$v.state.required) {
          return 'required'
        }
        return null
      },
      countyError() {
        if (!this.$v.county.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      isLocatedInUS() {
        return this.locatedInUS === 'yes'
      },
      isForeignOrUS() {
        return this.locatedInUS === 'yes' || this.locatedInUS === 'no'
      },
      computedAddresses() {
        return this.internalAddresses
          .map(address => {
            let phoneExtension = (address.workPhoneNumberExt ? ` +${address.workPhoneNumberExt}` : '')
            address.workPhoneNumber = address.workPhoneNumber + phoneExtension
            address.primary = convertAddressType(address) === PRIMARY_ADDRESS_CODE
            return address
          })
          .sort((addressA, addressB) => {
            if (addressA.primary === addressB.primary) return 0
            else if (addressA.primary) return -1
            else return -1
          })
      },
      computedIsFirstAddress() {
        return this.internalAddresses.length === 0
      },
      computedAddressFormVisible() {
        return this.internalShowForm || this.computedIsFirstAddress
      },
      showUpdateButton() {
        const hasAccess = this.editable && !this.editModeEnabled
        const canEditAddressType = this.addressType === PRIMARY_ADDRESS_CODE ? this.canEditHomeAddress : true

        return hasAccess && canEditAddressType
      },
      computedFieldsDisabled() {
        return !this.editable ||
          !this.editModeEnabled ||
          (!this.canEditHomeAddress && this.addressType === PRIMARY_ADDRESS_CODE)
      }
    },
    watch: {
      locatedInUS(newVal) {
        this.useLocatedInUSDefaultValue = false
      },
      addressLine1Required(newVal) {
        this.internalAddressLine1Required = newVal
      },
      value(newVal, oldVal) {
        this.firstName = newVal.firstName || ''
        this.middleInitial = newVal.middleInitial || ''
        this.lastName = newVal.lastName || ''
        this.suffix = newVal.suffix || ''
        this.email = newVal.email || ''
        this.phone = newVal.phone || ''
        this.line1 = newVal.line1 || ''
        this.line2 = newVal.line2 || ''
        this.line3 = newVal.line3 || ''

        // after the first time the user changes the locatedInUS field, we no longer want to
        // default it back to true
        const defaultLocatedInUs = this.useLocatedInUSDefaultValue ? 'yes' : ''

        this.locatedInUS = foreignIndicatorToLocatedInUS(newVal.foreignIndicator, newVal.locatedInUS, defaultLocatedInUs)

        this.zip = newVal.zip || ''
        this.city = newVal.city || ''
        this.state = newVal.state || ''
        this.county = newVal.county || ''
        this.addressType = convertAddressType(newVal)
      },
      addresses(newAddresses, oldAddresses) {
        this.internalAddresses = newAddresses
      }
    },
    created() {
      // locatedInUS could have been transformed in the data() when created, make sure
      // we emit that this was updated
      if (this.locatedInUS !== this.value.locatedInUS) {
        this.addressUpdated()
      }
    }
  }
</script>
